//? This file contains the API functions for the AnswerSheets.
import { api } from "../../../services/apis/configs/axiosConfigs";

//! Importing the API_URI constant
import { API_URI } from "../../../constants/consts";

//? Importing types from the answerSheetsAPI file
import {
  type AnswerSheet,
  type GetAnswerSheetsResponse,
  type CreateAnswerSheetInput,
  type AnswerSheetDetails,
} from "./types/types";

/**
 * AnswerSheet API
 */
export const answerSheetAPI = {
  /**
   * ? Fetches all AnswerSheets with search and pagination support.
   *
   * @param applicationId - ID da aplicação.
   * @param search - Optional search term.
   * @param page - Page number for pagination.
   * @param token - Authentication token.
   * @param perPage - Number of items per page.
   * @returns A promise that resolves to GetAnswerSheetsResponse.
   */
  getAll: async function (
    applicationId: string,
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetAnswerSheetsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications/${applicationId}/answer-sheets?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;
        const pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          answerSheets: data.answerSheets || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching answer sheets:", error);
        throw new Error("Failed to fetch answer sheets");
      });
  },

  /**
   * TODO: Fetches a specific AnswerSheet by ID.
   *
   * @param id - AnswerSheet ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to AnswerSheetDetails.
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<AnswerSheetDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/answer-sheets/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching answer sheet:", error);
        throw new Error("Failed to fetch answer sheet");
      });
  },

  /**
   * ! Creates a new AnswerSheet.
   *
   * @param data - Data to create the AnswerSheet.
   * @param token - Authentication token.
   * @param applicationId - ID da aplicação.
   * @returns A promise that resolves to AnswerSheet.
   */
  create: async function (
    data: CreateAnswerSheetInput,
    token: string,
    applicationId: string
  ): Promise<AnswerSheet> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/answer-sheets`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        if (
          error.response?.data?.error?.message?.includes(
            "Nenhum estudante encontrado para a aplicação"
          )
        ) {
          throw new Error(
            "Não há estudantes cadastrados para esta aplicação. Registre estudantes antes de prosseguir."
          );
        }
        console.error("Error creating answer sheet:", error, data);
        throw error;
      });
  },

  /**
   * ! Updates an existing AnswerSheet.
   *
   * @param id - AnswerSheet ID.
   * @param data - Data to update the AnswerSheet.
   * @param token - Authentication token.
   * @returns A promise that resolves to AnswerSheet.
   */
  update: async function (
    id: string,
    data: CreateAnswerSheetInput,
    token: string
  ): Promise<AnswerSheet> {
    return api
      .request({
        url: `${API_URI}/admin/private/answer-sheets/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating answer sheet:", error);
        throw new Error("Failed to update answer sheet");
      });
  },

  /**
   * ? Deletes a specific AnswerSheet by ID.
   *
   * @param id - AnswerSheet ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to void.
   */
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/answer-sheets/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting answer sheet:", error);
        throw new Error("Failed to delete answer sheet");
      });
  },

  /**
   * Fetches the status of an answer sheet generation job.
   *
   * @param id - Job ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to JobStatus.
   */
  getStatusJob: async function (
    id: string, 
    token: string
  ): Promise<{ job_status: string; pdf_url: string | null; error_message: string | null }> {
    try {
      const response = await api.request({
        url: `${API_URI}/admin/private/answer-sheets-generation/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Properly extract the data from the response structure
      return response.data.data;
    } catch (error: any) {
      console.error("Error getting status job:", error);

      const errorMessage = error.response?.data?.error?.message;

      if (
        errorMessage?.includes("Nenhum estudante encontrado para a aplicação")
      ) {
        throw new Error(
          "Não há estudantes cadastrados para esta aplicação. Registre estudantes antes de prosseguir."
        );
      }

      throw new Error(errorMessage || "Erro ao buscar o status do job.");
    }
  },
};
